
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import MainLayout from "@/components/layouts/MainLayout";
import ClientsTable from "@/components/clients/ClientsTable";
import ClientsSearch from "@/components/clients/ClientsSearch";
import ClientForm from "@/components/clients/ClientForm";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/types/supabase";

type Client = Database["public"]["Tables"]["clients"]["Row"];
type Profile = {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
};

const MyClients = () => {
  const navigate = useNavigate();
  const { user, isSeller, isAdmin } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // NUEVO: Para admins, lista de vendedores y selección de uno.
  const [sellers, setSellers] = useState<Profile[]>([]);
  const [newClientSellerId, setNewClientSellerId] = useState<string>("");

  useEffect(() => {
    // Permitir acceso a vendedores y administradores
    if (!isSeller && !isAdmin) {
      navigate("/dashboard");
      return;
    }
    loadClients();
    if (isAdmin) fetchSellers();
  }, [user?.id, isSeller, isAdmin, navigate]);

  useEffect(() => {
    // Filtrar clientes basado en el término de búsqueda
    if (searchTerm.trim() === "") {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.rnc.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  }, [clients, searchTerm]);

  // NUEVO: cargar vendedores si soy admin
  const fetchSellers = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, email, role")
      .eq("role", "seller")
      .order("full_name");

    if (error) {
      toast.error("Error al cargar vendedores");
      return;
    }
    setSellers(data || []);
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("clients")
        .select(`
          *,
          commission_types:commission_type_id (
            id,
            name,
            display_name,
            percentage
          )
        `);
      if (!isAdmin) {
        query = query.eq("seller_id", user?.id);
      }
      const { data, error } = await query.order("name");
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      toast.error("Error al cargar los clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);

      // Verificar RNC único
      const { data: existingClient } = await supabase
        .from("clients")
        .select("id, rnc")
        .eq("rnc", formData.rnc)
        .single();

      if (existingClient && existingClient.id !== selectedClient?.id) {
        toast.error("Ya existe un cliente con este RNC");
        return;
      }

      if (selectedClient) {
        // Actualizar cliente
        const { error } = await supabase
          .from("clients")
          .update(formData)
          .eq("id", selectedClient.id);

        if (error) throw error;
        toast.success("Cliente actualizado correctamente");
      } else {
        // NUEVO: Administrador o vendedor puede crear
        let sellerId = user?.id;
        if (isAdmin) {
          if (!newClientSellerId) {
            toast.error("Debes seleccionar el vendedor responsable");
            return;
          }
          sellerId = newClientSellerId;
        }
        const { error } = await supabase.from("clients").insert([
          {
            ...formData,
            seller_id: sellerId,
          },
        ]);

        if (error) throw error;
        toast.success("Cliente creado correctamente");
      }

      setIsDialogOpen(false);
      setSelectedClient(null);
      setNewClientSellerId(""); // limpiamos el selector
      loadClients();
    } catch (error) {
      console.error("Error al guardar cliente:", error);
      toast.error("Error al guardar el cliente");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cuando se abre el modal de nuevo cliente, reiniciamos el sellerId.
  const handleAddClient = () => {
    setSelectedClient(null);
    setNewClientSellerId("");
    setIsDialogOpen(true);
  };

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsDialogOpen(true);
  };

  const handleDelete = async (client: Client) => {
    if (!confirm("¿Está seguro de eliminar este cliente?")) return;

    try {
      const { error } = await supabase
        .from("clients")
        .delete()
        .eq("id", client.id);

      if (error) throw error;

      toast.success("Cliente eliminado correctamente");
      loadClients();
    } catch (error) {
      console.error("Error al eliminar cliente:", error);
      toast.error("Error al eliminar el cliente");
    }
  };

  if (!isSeller && !isAdmin) {
    return null;
  }

  return (
    <MainLayout>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {isAdmin ? "Todos los Clientes" : "Mis Clientes"}
              </CardTitle>
              <CardDescription>
                {isAdmin 
                  ? "Visualiza y gestiona todos los clientes del sistema" 
                  : "Gestiona tus clientes y sus datos"
                }
              </CardDescription>
            </div>
            {(isSeller || isAdmin) && (
              <Button onClick={handleAddClient}>
                <Plus className="mr-2 h-4 w-4" />
                Añadir cliente
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <ClientsSearch
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          </div>
          {loading ? (
            <div className="text-center py-4">Cargando clientes...</div>
          ) : (
            <ClientsTable
              clients={filteredClients}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient ? "Editar cliente" : "Nuevo cliente"}
            </DialogTitle>
          </DialogHeader>
          {/* Si es admin y agregando, mostrar selección de vendedor */}
          {!selectedClient && isAdmin && (
            <div className="mb-4 space-y-2">
              <Label className="text-sm font-medium">Vendedor responsable</Label>
              <Select
                value={newClientSellerId}
                onValueChange={setNewClientSellerId}
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un vendedor..." />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map(seller => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.full_name || seller.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <ClientForm
            onSubmit={handleSubmit}
            initialData={selectedClient || undefined}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default MyClients;
